import React, { useState, useMemo, useCallback } from 'react'
import {
  CompanyId,
  DeleteUser$Request,
  DivisionDto,
  DivisionId,
  DivisionCategoryId,
  DivisionRow,
  GetSpecificCompanyUsers$Request,
  SetUserDivision$Request,
  ToggleUserStatus$Request,
  UserCompanyDto,
  UserDto,
  UserId,
} from '../../../../services/api.types'
import {
  MRT_Column,
  type MRT_ColumnDef,
  MRT_GlobalFilterTextField,
  MRT_RowSelectionState,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table'
import { t } from 'ttag'
import { Box } from '@material-ui/core'
import { darkBlue, gray, primaryBlue } from '../../../../styles/material-ui-theme-v2'

import YumTeamSelect from '../../../../components/YumTeamSelect/YumTeamSelect'
import YumGroupSelect from '../../../../components/YumGroupSelect/YumGroupSelect'
import YumTable from '../../../../components/YumTable/YumTable'

export interface ITeamMembersTablePropsDispatchProps {
  getCompanyUsers(payload: GetSpecificCompanyUsers$Request): void
  getCompanyDivisions(payload?: { companyId: CompanyId }): void
  toggleUserStatus(payload: ToggleUserStatus$Request): void
  setUserDivision(payload: SetUserDivision$Request): void
  setUserCategoryDivision(payload: {
    companyId: CompanyId
    userId: UserId
    categoryId: DivisionCategoryId
    divisionId: DivisionId | null
    isAdminView?: boolean
  }): void
  deleteUser(payload: DeleteUser$Request): void
  setCompanyUsersLoading(): void
}

export interface ITeamMembersTableProps extends ITeamMembersTablePropsDispatchProps {
  companyId: CompanyId
  divisionId: DivisionId
  categoryId?: DivisionCategoryId
  //companyGroups?: { [companyId: string]: { [categoryId: string]: DivisionRow[] } }
  companyUsers: UserDto[]
  divisions: DivisionDto[]
  usersLoading: boolean
  managedCompanies: UserCompanyDto[]
  createdDivisionId: DivisionId | null
  deletedDivisionIds: DivisionId[]
}

export default function TeamMembersTable(props: ITeamMembersTableProps) {
  const {
    companyId,
    divisionId,
    categoryId,
    //companyGroups,
    usersLoading,
    companyUsers,
    divisions,
    createdDivisionId,
    deletedDivisionIds,
    setUserDivision,
    setUserCategoryDivision,
  } = props

  //const [companyDivisions, setCompanyDivisions] = useState({} as any)
  const [userIdInDeletion, setUserIdInDeletion] = useState(null as UserId | null)
  const [fullNameInDeletion, setFullNameIdInDeletion] = useState('' as string)

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

  const loadCompanyUsers = () => {
    const { getCompanyUsers } = props
    // For TeamMembersTable, we want to get users with their division assignments
    // The divisionIds parameter will be handled by the backend to include division assignments
    getCompanyUsers({ companyId })
  }

  // Helper function to check if user is assigned to the specific division within the category
  const isUserInDivision = useCallback(
    (user: UserDto): boolean => {
      if (!categoryId) {
        // If no categoryId, use the old divisionId-based filtering (backward compatibility)
        return user.divisionId === divisionId
      }

      // If categoryId is present, check the divisionIds JSON string for category-based assignments
      if (!user.divisionIds) {
        // Fallback: if no divisionIds JSON, check if user's regular divisionId matches
        // This handles cases where users haven't been migrated to the new category system yet
        // console.log(
        //   'User has no divisionIds JSON, falling back to regular divisionId:',
        //   user.divisionId,
        //   'target:',
        //   divisionId,
        // )
        return user.divisionId === divisionId
      }

      try {
        const divisionAssignments = JSON.parse(user.divisionIds)
        // console.log(
        //   'User divisionAssignments:',
        //   divisionAssignments,
        //   'categoryId:',
        //   categoryId,
        //   'divisionId:',
        //   divisionId,
        // )
        // Check if user is assigned to the specific division within the category
        // divisionAssignments structure: { "categoryId": "divisionId" }
        const isAssigned = divisionAssignments[categoryId] === divisionId
        //console.log('User assignment result:', isAssigned)
        return isAssigned
      } catch (error) {
        //console.warn('Failed to parse user divisionIds:', error, 'user:', user)
        // Fallback: if JSON parsing fails, check regular divisionId
        return user.divisionId === divisionId
      }
    },
    [categoryId, divisionId],
  )

  // Legacy componentDidMount hook equivalent.
  React.useEffect(() => {
    loadCompanyUsers()
  }, [])

  /*React.useEffect(() => {
    //console.log('Setting companyDivisions - categoryId:', categoryId, 'companyGroups:', companyGroups)
    if (categoryId && companyGroups && companyGroups[companyId] && companyGroups[companyId][categoryId]) {
      // If categoryId is present, use category-specific groups for the current company
      //console.log('Using category-specific groups:', companyGroups[companyId][categoryId])
      setCompanyDivisions(companyGroups[companyId][categoryId])
    } else {
      // If no categoryId, use all company divisions (backward compatibility)
      const filteredDivisions = divisions.filter((division) => division.companyId === companyId)
      //console.log('Using all company divisions:', filteredDivisions)
      setCompanyDivisions(filteredDivisions)
    }
  }, [companyId, divisions, categoryId, companyGroups])*/

  // Load divisions when new teams are created or old ones deleted
  React.useEffect(() => {
    const { getCompanyDivisions } = props
    getCompanyDivisions({ companyId })
  }, [createdDivisionId, deletedDivisionIds])

  const handleDivisionChange = useCallback(
    (userId: UserId) => (event: React.ChangeEvent<{ value: unknown }>) => {
      const { value } = event.target as { value: DivisionId }

      if (categoryId) {
        // If categoryId is present, use category-based assignment
        setUserCategoryDivision({
          companyId,
          userId,
          categoryId,
          divisionId: value || null,
          isAdminView: true,
        })
      } else {
        // If no categoryId, use the old division-based assignment (backward compatibility)
        setUserDivision({ companyId, userId, divisionId: value || null })
      }
    },
    [categoryId, companyId, setUserCategoryDivision, setUserDivision],
  )

  const columns = useMemo<MRT_ColumnDef<UserDto>[]>(
    () => [
      {
        accessorKey: 'firstName',
        header: t`User`,
        accessorFn: (row) => row.firstName + ' ' + row.lastName + ' ' + row.email,
        Cell: ({ cell }: any) => {
          const user = cell?.row?.original as UserDto
          const { firstName, lastName, email } = user
          return (
            <>
              {firstName && lastName ? (
                <div style={{ lineHeight: '20px' }}>
                  {firstName} {lastName}
                </div>
              ) : (
                ''
              )}
              <div style={{ lineHeight: '20px' }}>
                <a style={{ textDecoration: 'none', color: primaryBlue }} href={`mailto:${email}`}>
                  {email}
                </a>
              </div>
            </>
          )
        },
      },
      {
        accessorKey: 'divisionId',
        header: categoryId ? t`Group` : t`Team`,
        Cell: ({
          row: {
            original: { divisionId: cellDivisionId, userId, maxTeamSize, divisionIds },
          },
        }: any) => {
          if (!divisions || !divisions.length) return ''
          if (categoryId) {
            // Create a handler for division category changes
            /*const handleCategoryDivisionChange =
              (categoryId: string) => (event: React.ChangeEvent<{ value: unknown }>) => {
                const { value } = event.target as { value: DivisionId }
                setUserCategoryDivision({
                  companyId,
                  userId,
                  categoryId,
                  divisionId: value || null,
                  isAdminView: true,
                })
              }*/

            // Parse the divisionIds JSON string to get the user's division assignments
            let divisionCategoryAssignments = []
            try {
              if (divisionIds && typeof divisionIds === 'string') {
                const parsed = JSON.parse(divisionIds)
                //console.log('Parsed divisionIds for user', userId, ':', parsed)
                // The structure might be { assignments: [...] } or just [...]
                divisionCategoryAssignments = parsed.assignments || parsed || []
              }
            } catch (error) {
              //console.warn('Failed to parse divisionIds:', error, 'Raw divisionIds:', divisionIds)
              divisionCategoryAssignments = []
            }
            //console.log('category.id', category.id)
            //console.log('divisionCategoryAssignments', divisionCategoryAssignments)

            // Get the user's current assignment for this category
            const selectedGroupId =
              divisionCategoryAssignments != null ? divisionCategoryAssignments?.[categoryId] : null

            // Use YumGroupSelect for category-based groups
            return (
              <>
                <YumGroupSelect
                  isAdmin={true}
                  maxGroupSize={maxTeamSize}
                  groups={divisions}
                  value={selectedGroupId || ''}
                  onChange={handleDivisionChange(userId)}
                  style={{
                    marginBottom: 0,
                  }}
                  verticalPadding={7}
                />
              </>
            )
          } else {
            // Use YumTeamSelect for regular teams (backward compatibility)
            return (
              <YumTeamSelect
                isAdmin={true}
                maxTeamSize={maxTeamSize}
                companyDivisions={divisions}
                value={cellDivisionId || ''}
                onChange={handleDivisionChange(userId)}
                style={{
                  marginBottom: 0,
                }}
                verticalPadding={7}
              />
            )
          }
        },
      },
    ],
    [divisions, categoryId, handleDivisionChange],
  )

  const filteredUsers = useMemo(() => {
    return companyUsers.filter(isUserInDivision)
  }, [companyUsers, isUserInDivision])

  return (
    <>
      <YumTable
        enableTopToolbar={false}
        columns={columns as any}
        getRowId={(row: any) => row.userId || row.id}
        data={filteredUsers}
        onRowSelectionChange={setRowSelection}
        initialState={{
          isLoading: usersLoading,
          sorting: [
            {
              id: 'firstName',
              desc: false,
            },
          ],
          density: 'compact',
        }}
        state={{ rowSelection }}
        muiTableBodyProps={{
          sx: (theme) => ({
            '& > tr:hover > td': {
              backgroundColor: 'rgba(0, 111, 255, 0.05)',
            },
            '& > tr > td': {
              '& .MuiFilledInput-root': {
                backgroundColor: 'white !important',
              },
            },
          }),
        }}
        muiTableProps={{
          sx: (theme) => ({
            backgroundColor: gray,
            color: darkBlue,
          }),
        }}
        muiTableBodyRowProps={{
          sx: (theme) => ({
            backgroundColor: gray,
            '& > td:first-child': {
              paddingLeft: '16px',
            },
            '& > td:last-child': {
              paddingRight: '16px',
            },
          }),
        }}
        muiTopToolbarProps={{
          sx: (theme) => ({
            backgroundColor: gray,
          }),
        }}
        muiTableHeadRowProps={{
          sx: (theme) => ({
            padding: '50px',
            backgroundColor: gray,
            '& > th:first-child': {
              paddingLeft: '16px',
            },
            '& > th:last-child': {
              paddingRight: '16px',
            },
          }),
        }}
        muiBottomToolbarProps={{
          sx: (theme) => ({
            backgroundColor: gray,
            '& .MuiTablePagination-toolbar': {
              marginRight: '-8px',
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.875rem !important',
              paddingBottom: '2px',
            },
            '& .MuiTablePagination-selectLabel': {
              fontSize: '0.875rem !important',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem !important',
              fontWeight: 'bold',
            },
          }),
        }}
        /*muiTableBodyCellProps={{
          sx: (theme) => ({
            paddingTop: '8px !important',
            paddingBottom: '8px !important',
            lineHeight: '40px !important',
            fontSize: '0.875rem !important',
            color: darkBlue,
          }),
        }}*/
        renderBottomToolbarCustomActions={({ table }) => (
          <Box display="flex" gridGap="xs" alignContent="end">
            <MRT_ToggleGlobalFilterButton table={table} />
            <MRT_GlobalFilterTextField table={table} />
          </Box>
        )}
      />
    </>
  )
}
